<div class="insemination-overview">
  <div class="overview-header">
    <h2>{{ 'insemination-advice.total-animals' | translate }}: {{ getTotalAnimalCount() }}</h2>
    <div class="overview-header-actions">
      <button
        class="settings-icon"
        mat-button
        (click)="openThresholdSettingsDialog(); $event.stopPropagation()"
      >
        <mat-icon>settings</mat-icon>
      </button>
      <button mat-raised-button color="primary" (click)="printReport()" class="print-button">
        <mat-icon>print</mat-icon>
        {{ 'insemination-advice.print-button' | translate }}
      </button>
    </div>
  </div>

  <div class="table-container">
    @if (isLoading() && advice().length === 0) {
      <div class="loading-spinner">
        <mat-spinner></mat-spinner>
      </div>
    } @else if (!isLoading() && advice().length === 0) {
      <agr-notification-empty
        [isEmpty]="true"
        [emptyTitle]="'insemination-advice.empty-title' | translate"
        [emptyDescription]="'insemination-advice.empty-description' | translate"
        [emptyImage]="'/assets/images/no-items-3.png'"
        [emptyActionIcon]="'refresh'"
        [emptyActionText]="'insemination-advice.refresh-data' | translate"
        [showEmptyAction]="true"
        (emptyAction)="refreshData()"
      ></agr-notification-empty>
    } @else {
      <agr-table-v2 [dataSource]="inseminationDataSource" class="insemination-table">
        <ng-template agrTableFiltersWrapper></ng-template>
        <ng-template agrTableTopBar></ng-template>
        <ng-container matColumnDef="warning">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let cow" [ngClass]="getRowClass(cow)">
            @if (shouldShowWarning(cow)) {
              <mat-icon class="warning-icon">warning</mat-icon>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="animalId">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'insemination-advice.columns.animal-id' | translate }}
          </th>
          <td mat-cell *matCellDef="let cow" [ngClass]="getRowClass(cow)">
            {{ cow.registrationNumber }}
          </td>
        </ng-container>

        <ng-container matColumnDef="earNumber">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="sortable-header"
            (click)="sortData('earNumber')"
            (keydown.enter)="sortData('earNumber')"
            (keydown.space)="sortData('earNumber'); $event.preventDefault()"
            tabindex="0"
          >
            <span class="header-content">
              {{ 'insemination-advice.columns.ear-number' | translate }}
              @if (sortColumn() === 'earNumber') {
                <mat-icon class="sort-icon">{{
                  sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward'
                }}</mat-icon>
              } @else {
                <mat-icon class="sort-icon unfold-icon">unfold_more</mat-icon>
              }
            </span>
          </th>
          <td mat-cell *matCellDef="let cow" [ngClass]="getRowClass(cow)">
            {{ cow.earNumber || '-' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'insemination-advice.columns.name' | translate }}
          </th>
          <td mat-cell *matCellDef="let cow" [ngClass]="getRowClass(cow)">
            {{ cow.name || '-' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="age">
          <th
            mat-header-cell
            *matHeaderCellDef
            class="sortable-header"
            (click)="sortData('age')"
            (keydown.enter)="sortData('age')"
            (keydown.space)="sortData('age'); $event.preventDefault()"
            tabindex="0"
          >
            <span class="header-content">
              {{ 'insemination-advice.columns.age' | translate }}
              @if (sortColumn() === 'age') {
                <mat-icon class="sort-icon">{{
                  sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward'
                }}</mat-icon>
              } @else {
                <mat-icon class="sort-icon unfold-icon">unfold_more</mat-icon>
              }
            </span>
          </th>
          <td mat-cell *matCellDef="let cow" [ngClass]="getRowClass(cow)">
            {{ cow.ageInMonths | number: '1.1-1' }} {{ 'insemination-advice.months' | translate }}
          </td>
        </ng-container>

        <ng-container matColumnDef="height">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'insemination-advice.columns.height' | translate }}
          </th>
          <td mat-cell *matCellDef="let cow" [ngClass]="getRowClass(cow)">
            {{ cow.heightCm ? cow.heightCm + ' cm' : '-' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="estimatedHeight">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'insemination-advice.columns.estimated-height' | translate }}
          </th>
          <td mat-cell *matCellDef="let cow" [ngClass]="getRowClass(cow)">
            {{ cow.estimatedHeightCm ? cow.estimatedHeightCm + ' cm' : '-' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="lastInsemination">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'insemination-advice.columns.last-insemination' | translate }}
          </th>
          <td mat-cell *matCellDef="let cow" [ngClass]="getRowClass(cow)">
            <div class="date-display">
              {{
                formatDate(cow.dateOfLastInsemination) ||
                  ('insemination-advice.status.no-last-insemination' | translate)
              }}
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="advice">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'insemination-advice.columns.advice' | translate }}
          </th>
          <td mat-cell *matCellDef="let cow" [ngClass]="getRowClass(cow)">
            {{ getAdviceText(cow) }}
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayColumns"></tr>
      </agr-table-v2>
    }
  </div>
  <insemination-print
    [printTableData]="printTableData"
    [measurementDate]="measurementDateForPrint"
    [alvaMetrics]="alvaMetrics()"
  ></insemination-print>
</div>
